import { ref, computed } from 'vue';
import type { FormStructure, FormField, Section } from '../types/formTypeStructure';

interface UseFormNavigationProps {
  form: FormStructure;
  fieldsPerPage?: number;
  paginationMode?: 'byFields' | 'bySection';
  hasPreviewSection?: boolean;
}

export function useFormNavigation({
  form,
  fieldsPerPage = 4,
  paginationMode = 'byFields',
  hasPreviewSection = false
}: UseFormNavigationProps) {
  const sectionIndex = ref(0);
  const fieldPage = ref(0);
  const sectionChangeCounter = ref(0);

  const currentSection = computed<Section | null>(() => {
    // Si on est sur la section de prévisualisation, retourner null
    if (hasPreviewSection && sectionIndex.value === form.sections.length) {
      return null;
    }
    return form.sections[sectionIndex.value];
  });
  const fields = computed<FormField[]>(() => currentSection.value?.formFields || []);

  // Détecter si la section courante contient un champ OTP
  const isOTPSection = computed(() => {
    if (!currentSection.value) return false;
    return currentSection.value.formFields?.some(field => field.fieldType === 'OTP') ?? false;
  });

  const totalFieldPages = computed(() => {
    // Exception : si la section contient un OTP, afficher tous les champs (comme bySection)
    if (isOTPSection.value) {
      return 1;
    }
    // Mode bySection : toujours 1 page
    if (paginationMode === 'bySection') {
      return 1;
    }
    // Mode byFields : pagination classique
    return Math.ceil(fields.value.length / fieldsPerPage);
  });

  const pagedFields = computed(() => {
    // Exception : si la section contient un OTP, afficher tous les champs (comme bySection)
    if (isOTPSection.value) {
      return fields.value;
    }
    // Mode bySection : on affiche tous les champs de la section
    if (paginationMode === 'bySection') {
      return fields.value;
    }
    // Mode byFields : pagination classique
    return fields.value.slice(
      fieldPage.value * fieldsPerPage,
      (fieldPage.value + 1) * fieldsPerPage
    );
  });

  const totalSteps = computed(() => {
    let baseSteps: number;
    if (paginationMode === 'bySection') {
      // Une étape par section
      baseSteps = form.sections.length;
    } else {
      // Mode byFields : mêmes calculs qu'avant
      baseSteps = form.sections.reduce((acc, section) => {
        return acc + Math.ceil((section.formFields?.length || 0) / fieldsPerPage);
      }, 0);
    }
    // Ajouter 1 si on a une section de prévisualisation
    return hasPreviewSection ? baseSteps + 1 : baseSteps;
  });

  const currentStep = computed(() => {
    // Si on est sur la section de prévisualisation
    if (hasPreviewSection && sectionIndex.value === form.sections.length) {
      return totalSteps.value;
    }

    if (paginationMode === 'bySection') {
      // Étape = index de section + 1
      return sectionIndex.value + 1;
    }

    let step = 0;
    for (let s = 0; s < sectionIndex.value; s++) {
      step += Math.ceil((form.sections[s].formFields?.length || 0) / fieldsPerPage);
    }
    return step + fieldPage.value + 1;
  });

  const isFirstSection = computed(() =>
    paginationMode === 'bySection'
      ? sectionIndex.value === 0
      : sectionIndex.value === 0 && fieldPage.value === 0
  );

  const isPreviewSection = computed(() => {
    return hasPreviewSection && sectionIndex.value === form.sections.length;
  });

  const isLastSection = computed(() => {
    // Si on a une section de prévisualisation, la dernière section réelle n'est pas la dernière
    if (hasPreviewSection) {
      // On est sur la dernière section réelle, mais pas encore sur la prévisualisation
      return false;
    }

    const isLastSectionIndex = sectionIndex.value === form.sections.length - 1;

    if (paginationMode === 'bySection') {
      // Mode bySection : juste la dernière section
      return isLastSectionIndex;
    }

    // Mode byFields : dernière section ET dernière page de cette section
    const isLastPageInSection = fieldPage.value === totalFieldPages.value - 1;
    return isLastSectionIndex && isLastPageInSection;
  });

  const fadeKey = computed(() => (sectionIndex.value * 10000) + (sectionChangeCounter.value * 1000) + fieldPage.value);

  function goToPrevious() {
    // Si on est sur la section de prévisualisation, revenir à la dernière section réelle
    if (hasPreviewSection && sectionIndex.value === form.sections.length) {
      sectionChangeCounter.value++;
      sectionIndex.value = form.sections.length - 1;
      if (paginationMode === 'bySection') {
        fieldPage.value = 0;
      } else {
        const lastFields = form.sections[sectionIndex.value].formFields || [];
        fieldPage.value = Math.max(0, Math.ceil(lastFields.length / fieldsPerPage) - 1);
      }
      return;
    }

    if (paginationMode === 'bySection') {
      if (sectionIndex.value > 0) {
        sectionChangeCounter.value++;
        sectionIndex.value--;
        fieldPage.value = 0;
      }
      return;
    }

    if (fieldPage.value > 0) {
      fieldPage.value--;
    } else if (sectionIndex.value > 0) {
      sectionChangeCounter.value++;
      sectionIndex.value--;
      const prevFields = form.sections[sectionIndex.value].formFields || [];
      fieldPage.value = Math.ceil(prevFields.length / fieldsPerPage) - 1;
    }
  }

  function goToNext() {
    // Si on est sur la dernière section réelle et qu'on a une section de prévisualisation, y aller
    if (hasPreviewSection && sectionIndex.value === form.sections.length - 1) {
      if (paginationMode === 'bySection') {
        // Mode bySection : passer directement à la prévisualisation
        sectionChangeCounter.value++;
        sectionIndex.value = form.sections.length;
        fieldPage.value = 0;
        return;
      } else {
        // Mode byFields : vérifier qu'on est sur la dernière page de la dernière section
        const isLastPageInSection = fieldPage.value === totalFieldPages.value - 1;
        if (isLastPageInSection) {
          sectionChangeCounter.value++;
          sectionIndex.value = form.sections.length;
          fieldPage.value = 0;
          return;
        }
      }
    }

    if (paginationMode === 'bySection') {
      if (sectionIndex.value < form.sections.length - 1) {
        sectionChangeCounter.value++;
        sectionIndex.value++;
        fieldPage.value = 0;
      }
      return;
    }

    if (fieldPage.value < totalFieldPages.value - 1) {
      fieldPage.value++;
    } else if (sectionIndex.value < form.sections.length - 1) {
      sectionChangeCounter.value++;
      sectionIndex.value++;
      fieldPage.value = 0;
    }
  }

  function goToFirst() {
    sectionChangeCounter.value++;
    sectionIndex.value = 0;
    fieldPage.value = 0;
  }

  function goToLast() {
    sectionChangeCounter.value++;
    sectionIndex.value = form.sections.length - 1;
    if (paginationMode === 'bySection') {
      fieldPage.value = 0;
    } else {
      const lastSection = form.sections[sectionIndex.value];
      const lastFields = lastSection.formFields || [];
      fieldPage.value = Math.max(0, Math.ceil(lastFields.length / fieldsPerPage) - 1);
    }
  }

  function goToStep(targetSection: number, targetPage = 0) {
    if (targetSection >= 0 && targetSection < form.sections.length) {
      if (targetSection !== sectionIndex.value) {
        sectionChangeCounter.value++;
      }
      sectionIndex.value = targetSection;
      const targetFields = form.sections[targetSection].formFields || [];
      const maxPage = Math.ceil(targetFields.length / fieldsPerPage) - 1;
      fieldPage.value = Math.max(0, Math.min(targetPage, maxPage));
    }
  }

  return {
    // état réactif
    sectionIndex,
    fieldPage,
    currentSection,
    pagedFields,

    // navigation
    goToPrevious,
    goToNext,
    goToFirst,
    goToLast,
    goToStep,

    // infos calculées
    isFirstSection,
    isLastSection,
    isPreviewSection,
    totalSteps,
    currentStep,
    fadeKey,

    // debug facultatif
    debugInfo: computed(() => ({
      sectionCount: form.sections.length,
      totalFieldPages: totalFieldPages.value,
      fieldsOnCurrentPage: pagedFields.value.length,
    }))
  };
}
